import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, XCircle, RefreshCw, Database, Server, Clock, Users, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const REFRESH_MS = 15000;

const statusConfig = {
  ok: {
    label: "Semua Sistem Beroperasi Normal",
    color: "bg-green-500",
    text: "text-green-700",
    bg: "bg-green-50",
    border: "border-green-200",
    Icon: CheckCircle2,
  },
  degraded: {
    label: "Sebagian Layanan Terganggu",
    color: "bg-amber-500",
    text: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
    Icon: AlertCircle,
  },
  down: {
    label: "Layanan Tidak Tersedia",
    color: "bg-red-500",
    text: "text-red-700",
    bg: "bg-red-50",
    border: "border-red-200",
    Icon: XCircle,
  },
  unreachable: {
    label: "Tidak Dapat Terhubung",
    color: "bg-zinc-500",
    text: "text-zinc-700",
    bg: "bg-zinc-100",
    border: "border-zinc-200",
    Icon: XCircle,
  },
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
};

const fmtAppDateShort = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
    return date;
  } catch { return iso; }
};

const fmtAppTimeShort = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) + " WIB";
  } catch { return iso; }
};

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [history, setHistory] = useState([]); // last 30 pings
  const timerRef = useRef(null);

  const fetchHealth = async () => {
    const t0 = performance.now();
    try {
      const r = await axios.get(`${API}/health`, { timeout: 10000 });
      const latency = Math.round(performance.now() - t0);
      setData(r.data);
      setError(null);
      setHistory((h) => [...h.slice(-29), { ok: r.data.status === "ok", latency, t: Date.now() }]);
    } catch (e) {
      const latency = Math.round(performance.now() - t0);
      setError(e?.message || "Network error");
      setData(null);
      setHistory((h) => [...h.slice(-29), { ok: false, latency, t: Date.now() }]);
    } finally {
      setLoading(false);
      setLastFetch(new Date().toISOString());
    }
  };

  useEffect(() => {
    fetchHealth();
    timerRef.current = setInterval(fetchHealth, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const overall = error ? "unreachable" : (data?.status || "unreachable");
  const cfg = statusConfig[overall];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="status-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Status</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">RelayLab Presensi</h1>
          </div>
          <Link to="/login" className="text-sm text-primary hover:underline" data-testid="link-login">
            Login →
          </Link>
        </div>

        {/* Big Status Banner */}
        <div className={`rounded-lg border-2 ${cfg.border} ${cfg.bg} p-6 sm:p-8 mb-6`} data-testid="status-banner">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full ${cfg.color} flex items-center justify-center shrink-0 shadow-md`}>
              <cfg.Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className={`font-display text-xl sm:text-2xl font-bold ${cfg.text} leading-tight`} data-testid="status-label">
                {cfg.label}
              </h2>
              <p className="text-sm text-zinc-600 mt-1">
                Terakhir diperiksa: <span className="font-mono">{fmtTime(lastFetch)}</span>
                {loading && <span className="ml-2 inline-flex items-center gap-1 text-xs text-zinc-500"><RefreshCw className="w-3 h-3 animate-spin" /> memuat</span>}
              </p>
            </div>
            <button
              onClick={fetchHealth}
              disabled={loading}
              className="hidden sm:flex w-10 h-10 rounded-full bg-white border border-zinc-200 hover:bg-zinc-50 items-center justify-center disabled:opacity-50"
              data-testid="btn-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Service Components */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold uppercase tracking-wide">Layanan</h3>
            <span className="text-xs text-zinc-500">Auto refresh tiap 15 detik</span>
          </div>

          <ServiceRow
            Icon={Server}
            name="API Server"
            description="Endpoint backend FastAPI"
            status={error ? "down" : "ok"}
            latency={data?.services?.api?.latency_ms}
            tid="svc-api"
          />
          <ServiceRow
            Icon={Database}
            name="Database (MongoDB)"
            description="Penyimpanan data presensi & user"
            status={data?.services?.database?.status || (error ? "down" : "unreachable")}
            latency={data?.services?.database?.latency_ms}
            errorMsg={data?.services?.database?.error}
            tid="svc-db"
          />
        </div>

        {/* Stats */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard Icon={Clock} label="Waktu Server" value={fmtAppTimeShort(data.app_time)} sub={data.sim_active ? "⚠ Simulasi" : fmtAppDateShort(data.app_time)} tid="stat-time" />
            <StatCard Icon={Users} label="Total User" value={data.stats?.users ?? "—"} sub="terdaftar" tid="stat-users" />
            <StatCard Icon={MapPin} label="Lokasi Aktif" value={data.stats?.active_locations ?? "—"} sub="geofence" tid="stat-locs" />
            <StatCard Icon={Server} label="Versi" value={data.version || "—"} sub="API" tid="stat-version" />
          </div>
        )}

        {/* Mini Uptime Chart */}
        {history.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-6" data-testid="uptime-history">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-sm font-semibold uppercase tracking-wide">Riwayat Sesi Ini</h3>
              <span className="text-xs text-zinc-500">{history.length} pengecekan terakhir</span>
            </div>
            <div className="flex items-end gap-1 h-12">
              {Array.from({ length: 30 }).map((_, i) => {
                const h = history[i];
                if (!h) {
                  return <div key={i} className="flex-1 bg-zinc-100 rounded-sm" style={{ height: "20%" }} />;
                }
                const heightPct = Math.max(15, Math.min(100, 100 - h.latency / 10));
                return (
                  <div
                    key={i}
                    title={`${new Date(h.t).toLocaleTimeString("id-ID")} · ${h.latency}ms · ${h.ok ? "OK" : "FAIL"}`}
                    className={`flex-1 rounded-sm transition-all ${h.ok ? "bg-green-500" : "bg-red-500"}`}
                    style={{ height: `${heightPct}%`, minHeight: "8px" }}
                  />
                );
              })}
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Hijau = OK · Merah = Gagal · Tinggi bar = kecepatan respon
            </p>
          </div>
        )}

        {/* Error message if any */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-800" data-testid="error-msg">
            <p className="font-medium">Gagal terhubung ke server:</p>
            <p className="font-mono text-xs mt-1">{error}</p>
            <p className="mt-2 text-xs">
              Coba cek koneksi internet Anda. Jika koneksi normal tapi tetap error, kemungkinan ada gangguan di server.
              Hubungi admin atau coba lagi beberapa menit kemudian.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-zinc-500 mt-8">
          <p>Halaman ini memantau status sistem presensi secara real-time.</p>
          <p className="mt-1">
            Mengalami masalah saat login? Cek halaman ini dulu sebelum panggil admin.
          </p>
        </div>
      </div>
    </div>
  );
}

const ServiceRow = ({ Icon, name, description, status, latency, errorMsg, tid }) => {
  const cfg = statusConfig[status] || statusConfig.unreachable;
  const isOk = status === "ok";
  return (
    <div className="flex items-center gap-4 px-5 py-4 border-b border-zinc-100 last:border-b-0" data-testid={tid}>
      <div className="w-10 h-10 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-zinc-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{name}</p>
        <p className="text-xs text-zinc-500">{description}</p>
        {errorMsg && <p className="text-xs text-red-600 font-mono mt-1 truncate">{errorMsg}</p>}
      </div>
      <div className="text-right shrink-0">
        <div className="flex items-center gap-2 justify-end">
          <span className={`w-2 h-2 rounded-full ${cfg.color}`} />
          <span className={`text-xs font-medium ${cfg.text}`}>
            {isOk ? "Operasional" : status === "down" ? "Mati" : "Tidak Diketahui"}
          </span>
        </div>
        {latency != null && (
          <p className="text-xs text-zinc-400 font-mono mt-0.5">{latency} ms</p>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ Icon, label, value, sub, tid }) => (
  <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={tid}>
    <Icon className="w-4 h-4 text-zinc-400 mb-1" />
    <p className="text-[0.6rem] uppercase tracking-wider text-zinc-500 font-medium">{label}</p>
    <p className="font-display font-bold text-base leading-tight truncate">{value}</p>
    {sub && <p className="text-[0.65rem] text-zinc-400 truncate">{sub}</p>}
  </div>
);
